import React, { useCallback, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { FiCheck, FiCopy, FiHome, FiMonitor } from "react-icons/fi";
import { useAuth } from "../hooks/useAuth";
import { localizedPath } from "../hooks/useLocalizedNavigate";
import LadowanieStrony from "./common/Ladowanie/LadowanieStrony";
import AccessDeniedScreen from "./AccessDeniedScreen/AccessDeniedScreen";
import PustyStan from "./common/PustyStan";
import { adresPozaPanelem, opisLinku } from "./common/Obszary/adresyObszarow";
import { tytulStrony } from "../config/obszary";
import { ROUTE_MAP } from "../constants/routes";
import { useTelefon } from "../utils/urzadzenieMobilne";
import s from "./ProtectedRoute.module.css";

// Brak uprawnień → PEŁNOEKRANOWY ekran „brak uprawnień" (nie ciche przekierowanie na pulpit).
// Moduł nie startuje: nie montuje się żaden komponent strony i nie leci żadne zapytanie o dane.
// permissionKey — klucz PERM, którego zabrakło: ekran wypełnia nim prośbę o dostęp (szuflada do IT).
// [S-6, 2026-09-09] `mozliwyWniosek` MUSI przejść przez tę osłonkę: gałąź „moduł wyłączony globalnie” podawała je od 2026-09-06,
// ale osłonka miała zamkniętą listę propsów i ekran odmowy dalej proponował wniosek do IT o rzecz, której IT nie ma jak spełnić.
const AccessDenied = ({ title, message, moduleLabel, reason, permissionKey, mozliwyWniosek }) => (
    <AccessDeniedScreen title={title} message={message} moduleLabel={moduleLabel} reason={reason} permissionKey={permissionKey}
        mozliwyWniosek={mozliwyWniosek} />
);

/**
 * Czy adres leży w panelu administracyjnym: sam `/admin` albo cokolwiek POD nim.
 * [ui1006 2026-10-06] Granica segmentu, nie goły przedrostek: `/admin-wzorzec/…` (strona wzorcowa zestawu, tylko tryb
 * deweloperski) nie jest panelem — ma się dać obejrzeć przy szerokości telefonu (odbiór wzorów na 375 px).
 */
export const czyAdresPanelu = (pathname) => {
  const sciezka = String(pathname ?? "");
  return [ROUTE_MAP.admin.pl, ROUTE_MAP.admin.en].some((panel) => sciezka === panel || sciezka.startsWith(`${panel}/`));
};

/** Zdania ekranu linku na telefonie — w jednym miejscu, żeby zapadka N-16 pilnowała tych samych słów, które widzi człowiek. */
export const zdanieLinkuNaTelefonie = (dopelniaczObiektu, tytul) => `To jest link do ${dopelniaczObiektu} w »${tytul}«.`;
export const ZDANIE_PANEL_TYLKO_NA_KOMPUTERZE = "Panel administracyjny działa tylko na komputerze — otwórz ten sam link na komputerze.";
export const ZDANIE_LINK_SKOPIOWANY = "Link jest w schowku. Wklej go w przeglądarce na komputerze.";
export const ZDANIE_KOPIOWANIE_NIEUDANE = "Nie udało się skopiować — zaznacz adres i skopiuj ręcznie.";

/**
 * Kopiowanie do schowka z ODPOWIEDZIĄ, czy się udało: nowe API, a gdy odmówi (brak zgody, starsza przeglądarka) — stare
 * polecenie na ukrytym polu. `true` tylko wtedy, gdy któraś droga naprawdę potwierdziła skopiowanie.
 */
const skopiujDoSchowka = async (tekst) => {
  try {
    await navigator.clipboard.writeText(tekst);
    return true;
  } catch {
    const pole = document.createElement("textarea");
    pole.value = tekst;
    pole.style.position = "fixed";
    pole.style.opacity = "0";
    document.body.appendChild(pole);
    pole.select();
    let udane = false;
    try { udane = document.execCommand("copy") === true; } catch { udane = false; }
    document.body.removeChild(pole);
    return udane;
  }
};

/**
 * [ui1006 2026-10-06, decyzja D-06] EKRAN LINKU NA TELEFONIE.
 *
 * Panel /admin zostaje na telefonie zamknięty (decyzja właściciela z 16.09 — blok niżej). BYŁO ŹLE: powiadomienie
 * o incydencie albo o kroku w procedurze prowadzi do jednej z czterech stron panelu, a telefon pokazywał ogólne
 * „Panel administracyjny tylko na komputerze” — człowiek nie wiedział nawet, CZEGO dotyczył link, który dostał.
 * TERAZ: gdy adres niesie obiekt jednej z czterech stron (incydent, alert, konto, procedurę, wniosek…), ekran mówi, czego
 * dotyczył link, i daje przycisk „Skopiuj link” — ten sam adres otworzy się na komputerze. Panel się nie montuje.
 *
 * Nazwę strony i rodzaj obiektu bierze z rejestru obszarów (`opisLinku`) — także dla starych linków zapisanych w bazie
 * (`?zakladka=incydenty&incydent=7`) i także przed przełączeniem strony na nowy układ.
 *
 * [poprawka po przeglądzie 2026-10-06] „Skopiowano link” mówimy TYLKO po udanym kopiowaniu. BYŁO: potwierdzenie stało
 * bezwarunkowo, a droga awaryjna nie sprawdzała wyniku — przy odmowie schowka ekran twierdził „Link jest w schowku”,
 * choć nic tam nie było. TERAZ przy porażce ekran pokazuje adres w polu do zaznaczenia i mówi, co zrobić.
 */
export const EkranLinkuNaTelefonie = ({ opis }) => {
  const location = useLocation();
  const navigate = useNavigate();
  /** `null` — jeszcze nie kopiowano; `"skopiowano"`; `"nieudane"` — schowek odmówił obiema drogami. */
  const [wynik, setWynik] = useState(null);
  const adres = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
  const skopiowano = wynik === "skopiowano";
  const nieudane = wynik === "nieudane";

  const skopiuj = useCallback(async () => {
    setWynik((await skopiujDoSchowka(adres)) ? "skopiowano" : "nieudane");
  }, [adres]);

  return (
    <div className={s.ekranLinku} data-testid="ekran-linku-na-telefonie">
      <PustyStan
        className={s.kartaLinku}
        ikona={<FiMonitor />}
        tytul={zdanieLinkuNaTelefonie(opis.obiekt.definicja.dopelniacz, tytulStrony(opis.rejestr))}
        opis={ZDANIE_PANEL_TYLKO_NA_KOMPUTERZE}
        akcja={{
          etykieta: skopiowano ? "Skopiowano link" : "Skopiuj link",
          ikona: skopiowano ? <FiCheck size={14} /> : <FiCopy size={14} />,
          onClick: skopiuj,
          testId: "ekran-linku-skopiuj",
        }}
      >
        <button type="button" className="ghostBtn" onClick={() => navigate(localizedPath("dashboard"))}>
          <FiHome size={14} /> Przejdź na pulpit
        </button>
      </PustyStan>
      <p className={s.potwierdzenie} role="status" aria-live="polite">
        {skopiowano ? ZDANIE_LINK_SKOPIOWANY : ""}
        {nieudane ? ZDANIE_KOPIOWANIE_NIEUDANE : ""}
      </p>
      {nieudane && (
        <input
          type="text"
          readOnly
          className={s.poleAdresu}
          value={adres}
          aria-label="Adres linku do skopiowania"
          data-testid="ekran-linku-adres"
          onFocus={(e) => e.target.select()}
        />
      )}
    </div>
  );
};

const ProtectedRoute = ({ requiredPermission, requiredType, restrictedModule, requireO365, moduleLabel, children }) => {
  const { user, loading, hasPermission, isModuleRestricted, isModuleGloballyHidden, isModuleContractGated } = useAuth();
  const location = useLocation();
  const telefon = useTelefon();

  // [S19 2026-09-14] Sprawdzanie sesji: stan ładowania na cały widok (kropki, pasek, czas) zamiast rozmytej
  // nakładki. To WŁASNY wskaźnik trasy — globalny GlobalLoader czyta LoadingContext i przy sprawdzaniu sesji milczy.
  if (loading) {
    return <LadowanieStrony wariant="ekran" etykieta="Sprawdzanie sesji…" />;
  }

  if (!user) {
    return <Navigate to={localizedPath("login")} replace />;
  }

  /* [HASLA-LOKALNE, decyzja właściciela 2026-09-27] Hasło tymczasowe od administratora albo po terminie ważności (45 dni):
     z KAŻDEGO chronionego adresu prowadzimy wyłącznie do ekranu zmiany hasła — serwer i tak odrzuca resztę wywołań
     kodem 403 „haslo_do_zmiany”. Flaga przychodzi z logowania i z /users/me (UserInfo.hasloDoZmiany). */
  if (user.hasloDoZmiany === true) {
    return <Navigate to={localizedPath("zmianaHasla")} replace />;
  }

  /* [zgłoszenie właściciela 2026-09-16] „Zablokuj wejście do sekcji admin z urządzeń mobilnych,
     urządzenie mobilne ma być dla usera do podstawowych prac (…) mój dział ma być."

     Blokada stoi TUTAJ, a nie przy poszczególnych trasach panelu, bo tras panelu jest kilkanaście
     i dochodzą nowe — warunek przy każdej z nich byłby listą do zapomnienia przy pierwszej nowej
     zakładce. Jedno miejsce zamyka /admin i wszystko pod nim, także zakładkę z pamięci przeglądarki
     i link z powiadomienia.

     To NIE jest brak uprawnień, więc ekran nie proponuje wniosku do IT: ta sama osoba wejdzie tu
     z komputera bez żadnego nadania. Reszta aplikacji (pulpit, zgłoszenia, Mój Dział) zostaje. */
  if (telefon && czyAdresPanelu(location.pathname)) {
    /* [poprawka po przeglądzie 2026-10-06] WEJŚCIE Z PANELU DO DRZEWA POZA PANELEM: `/admin/wykaz-ksc/<cokolwiek>` to ten sam
       ekran co `/wykaz-ksc/<to samo>`, a Wykaz KSC na telefonie DZIAŁA. BYŁO: blokada panelu stała przed routerem, który
       robi to przekierowanie — człowiek czytał „otwórz ten sam link na komputerze”, co było nieprawdą. TERAZ osłona sama
       przenosi pod adres poza panelem (z zapytaniem i kotwicą); o wejście pyta osłona trasy docelowej.
       Samo `/admin/wykaz-ksc` (start strony W panelu, z przełącznikiem stron) zostaje zamknięte jak reszta panelu. */
    const pozaPanelem = adresPozaPanelem(location.pathname);
    if (pozaPanelem) {
      return <Navigate to={`${pozaPanelem}${location.search}${location.hash}`} replace />;
    }
    /* [ui1006] Link do KONKRETNEJ rzeczy na jednej z czterech stron: panel dalej się nie montuje, ale ekran mówi,
       czego dotyczył link (zamiast ogólnej odmowy) i pozwala go skopiować. Zwykłe adresy panelu — jak dotąd, niżej. */
    const opisLinkuPanelu = opisLinku({ pathname: location.pathname, search: location.search, state: location.state });
    if (opisLinkuPanelu && opisLinkuPanelu.obiekt) {
      return <EkranLinkuNaTelefonie opis={opisLinkuPanelu} />;
    }
    return (
      <AccessDenied
        title="Panel administracyjny tylko na komputerze"
        message="Administracja aplikacji jest wyłączona na telefonie. Ta sama sekcja otworzy się na komputerze — bez żadnych dodatkowych nadań."
        moduleLabel={moduleLabel}
        reason="Decyzja właściciela aplikacji: telefon służy do bieżącej pracy (pulpit, zgłoszenia, Mój Dział), a nie do konfiguracji systemu."
        mozliwyWniosek={false}
      />
    );
  }

  if (requiredType != null && user.type !== requiredType) {
    return <AccessDenied moduleLabel={moduleLabel} reason="Ta sekcja wymaga innego typu konta." />;
  }

  // Wymaga konta Office 365 (user.domain = bool isO365). Konta lokalne nie mają tej zakładki.
  if (requireO365 && user.domain !== true) {
    return (
      <AccessDenied
        message="Ta zakładka jest dostępna wyłącznie dla kont Office 365."
        moduleLabel={moduleLabel}
        reason="Twoje konto nie jest kontem Office 365."
      />
    );
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    const keys = Array.isArray(requiredPermission) ? requiredPermission.join(" lub ") : requiredPermission;
    return (
      <AccessDenied
        moduleLabel={moduleLabel}
        permissionKey={keys}
        reason={`Wymagane uprawnienie: ${keys}.`}
      />
    );
  }

  // [S-6, polecenie właściciela 2026-09-09] Zakładka poza formą zatrudnienia (Moja ścieżka kariery / Moje cele) — to nie
  // „ukrycie przez administratora”, tylko decyzja kadr w konfiguratorze; ekran mówi, kto o tym decyduje i że nie ma czego
  // nadawać (wniosek do IT nic tu nie zmieni). Sprawdzane PRZED ogólnym ukryciem, bo isModuleRestricted obejmuje oba.
  if (restrictedModule && typeof isModuleContractGated === "function" && isModuleContractGated(restrictedModule)) {
    return (
      <AccessDenied
        title="Zakładka poza Twoją formą zatrudnienia"
        message="Ta zakładka nie jest prowadzona dla Twojej formy zatrudnienia."
        moduleLabel={moduleLabel}
        reason="O tym, które formy zatrudnienia mają tę zakładkę, decyduje dział kadr (Konfigurator HR → Warianty umów)."
        mozliwyWniosek={false}
      />
    );
  }

  // Moduł menu niedostępny → brak wejścia nawet z linku:
  // (a) ukryty dla tego użytkownika (deny-list per user) LUB (b) wyłączony globalnie (ModuleVisibility).
  if (restrictedModule && isModuleRestricted(restrictedModule)) {
    return (
      <AccessDenied
        title="Moduł ukryty"
        message="Ten moduł został ukryty dla Twojego konta."
        moduleLabel={moduleLabel}
        reason="Widoczność modułu wyłączona indywidualnie dla tego użytkownika."
      />
    );
  }
  if (restrictedModule && isModuleGloballyHidden(restrictedModule)) {
    return (
      <AccessDenied
        title="Moduł wyłączony"
        message="Ten moduł jest obecnie wyłączony w całej aplikacji."
        moduleLabel={moduleLabel}
        reason="Globalna widoczność modułu wyłączona przez administratora."
        /* [polecenie właściciela 2026-09-06, pkt 1] Tu NIE MA czego nadać — nie brakuje uprawnienia,
           tylko administrator zamknął moduł wszystkim. Zgłoszenie do IT byłoby prosbą o rzecz,
           której IT nie ma jak spelnic. */
        mozliwyWniosek={false}
      />
    );
  }

  return children ? children : <Outlet />;
};

export default ProtectedRoute;
